/**
 * Who a staff invitation is for (ADR 0034: one person per human). The
 * administrator says so explicitly: someone already in People, found by a
 * search they type and chosen from the register, or a new person. Nothing is
 * suggested or matched from a typed name, email, or domain. People who
 * already sign in, or whose invitation is still open, are listed with the
 * reason and cannot be chosen: the server refuses both.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { RELATIONSHIP_LABEL } from "@/components/people/person-record";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { PEOPLE_QUERY_KEY, peopleApi, type PeopleRegisterRow } from "@/lib/api/people";
import { personChoice } from "@/lib/person-name";

import { staffInvitationRefusal, type StaffInvitationPersonMode } from "./staff-invitation-person";

const RESULT_LIMIT = 8;

function personMeta(person: PeopleRegisterRow): string {
  return [
    person.email,
    person.sid ? `SID ${person.sid}` : null,
    person.relationships.map((relationship) => RELATIONSHIP_LABEL[relationship]).join(" + ") || null,
  ].filter(Boolean).join(" · ");
}

export function StaffInvitationPerson({
  mode,
  person,
  onModeChange,
  onPersonChange,
}: {
  mode: StaffInvitationPersonMode | null;
  person: PeopleRegisterRow | null;
  onModeChange: (mode: StaffInvitationPersonMode) => void;
  onPersonChange: (person: PeopleRegisterRow | null) => void;
}) {
  const fieldId = useId();
  const [search, setSearch] = useState("");
  const settled = useDebouncedValue(search.trim());
  const searchInput = useRef<HTMLInputElement | null>(null);
  const changeButton = useRef<HTMLButtonElement | null>(null);
  // Choosing or changing the person replaces the control that had focus.
  const focusNext = useRef<"change" | "search" | null>(null);

  useEffect(() => {
    if (focusNext.current === "change") changeButton.current?.focus();
    if (focusNext.current === "search") searchInput.current?.focus();
    focusNext.current = null;
  }, [person]);

  const results = useQuery({
    queryKey: [...PEOPLE_QUERY_KEY, "staff-invitation-person", settled],
    queryFn: () => peopleApi.list({ view: "all", q: settled, page_size: RESULT_LIMIT }),
    enabled: mode === "existing" && !person && settled.length > 0,
  });
  const rows = results.data?.rows ?? [];

  return (
    <div className="grid gap-4">
      <RadioGroup
        aria-label="Who this invitation is for"
        value={mode ?? ""}
        onValueChange={(value) => onModeChange(value as StaffInvitationPersonMode)}
        className="gap-1 sm:grid-cols-2"
      >
        <ModeChoice
          id={`${fieldId}-existing`}
          value="existing"
          title="Someone in People"
          detail="Search the register and choose their record. When they sign in, the login joins it."
        />
        <ModeChoice
          id={`${fieldId}-new`}
          value="new"
          title="New person"
          detail="Not in People yet. Accepting the invitation gives them a record."
        />
      </RadioGroup>

      {mode === "existing" && person && (
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-control bg-well p-3">
          <div className="grid min-w-0 gap-0.5 [overflow-wrap:anywhere]">
            <span className="font-medium text-foreground">{personChoice(person.display_name, person.email)}</span>
            {personMeta(person) && (
              <span className="text-meta text-muted-foreground">{personMeta(person)}</span>
            )}
          </div>
          <Button
            ref={changeButton}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              focusNext.current = "search";
              onPersonChange(null);
            }}
          >
            Change person
          </Button>
        </div>
      )}

      {mode === "existing" && !person && (
        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor={`${fieldId}-search`}>Search People</Label>
            <Input
              ref={searchInput}
              id={`${fieldId}-search`}
              type="search"
              autoComplete="off"
              placeholder="Name, email, or student ID"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                // Enter searches as typed; it never submits the invitation.
                if (event.key === "Enter") event.preventDefault();
              }}
            />
          </div>
          <div aria-live="polite" className="grid gap-2 empty:hidden">
            {settled.length > 0 && <>
              {results.isLoading && <p className="text-meta text-muted-foreground">Searching People</p>}
              {results.isError && (
                <div className="flex flex-wrap items-center gap-3 text-meta">
                  <p className="text-destructive">People could not be searched.</p>
                  <Button type="button" size="sm" variant="outline" onClick={() => void results.refetch()}>
                    Try again
                  </Button>
                </div>
              )}
              {results.isSuccess && rows.length === 0 && (
                <p className="text-meta text-muted-foreground">
                  No one in People matches. Search again, or choose New person.
                </p>
              )}
              {rows.length > 0 && (
                <ul aria-label="Matching people" className="grid">
                  {rows.map((row) => {
                    const refusal = staffInvitationRefusal(row);
                    return (
                      <li key={row.person_id} className="border-t border-rule first:border-t-0">
                        <button
                          type="button"
                          aria-disabled={refusal ? true : undefined}
                          className="flex min-h-11 w-full min-w-0 items-start justify-between gap-3 px-2 py-2.5 text-left outline-none transition-colors duration-[var(--motion-fast)] hover:bg-well focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring aria-disabled:cursor-not-allowed aria-disabled:hover:bg-transparent"
                          onClick={() => {
                            if (refusal) return;
                            focusNext.current = "change";
                            onPersonChange(row);
                          }}
                        >
                          <span className="grid min-w-0 gap-0.5 [overflow-wrap:anywhere]">
                            <span className={refusal ? "text-ui text-muted-foreground" : "text-ui font-medium text-foreground"}>
                              {personChoice(row.display_name, row.email)}
                            </span>
                            {personMeta(row) && (
                              <span className="text-meta text-muted-foreground">{personMeta(row)}</span>
                            )}
                          </span>
                          <span className={refusal ? "shrink-0 text-meta text-muted-foreground" : "shrink-0 text-meta font-semibold text-primary"}>
                            {refusal ?? "Choose"}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {results.data && results.data.total > rows.length && (
                <p className="text-meta text-muted-foreground">
                  Showing {rows.length} of {results.data.total}. Search more precisely to find someone else.
                </p>
              )}
            </>}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeChoice({ id, value, title, detail }: {
  id: string;
  value: StaffInvitationPersonMode;
  title: string;
  detail: string;
}) {
  return (
    <Label
      htmlFor={id}
      className="flex min-h-11 cursor-pointer items-start gap-3 rounded-control px-3 py-3 text-left hover:bg-well has-[[data-state=checked]]:bg-primary-wash"
    >
      <RadioGroupItem
        id={id}
        value={value}
        className="relative -my-1.5 size-11 border-0 shadow-none before:absolute before:top-1/2 before:left-1/2 before:size-4 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:border before:border-input"
      />
      <span className="grid gap-0.5">
        <span className="text-ui font-medium text-foreground">{title}</span>
        <span className="text-meta font-normal leading-normal text-muted-foreground">{detail}</span>
      </span>
    </Label>
  );
}
